/**
 * i18n Configuration
 * 
 * Configuración de internacionalización usando i18next.
 * 
 * Estructura:
 * - Español (es) como idioma base
 * - Preparado para expandir a múltiples locales (es-CL, es-MX, en-US, etc.)
 * - Namespaces por dominio (common, auth, employee, attendance, company)
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Importar traducciones por namespace (Español Chile como base)
import commonEsCL from './locales/es-CL/common.json' with { type: 'json' };
import authEsCL from './locales/es-CL/auth.json' with { type: 'json' };
import employeeEsCL from './locales/es-CL/employee.json' with { type: 'json' };
import attendanceEsCL from './locales/es-CL/attendance.json' with { type: 'json' };
import companyEsCL from './locales/es-CL/company.json' with { type: 'json' };
import absenceEsCL from './locales/es-CL/absence.json' with { type: 'json' };
import authorizationEsCL from './locales/es-CL/authorization.json' with { type: 'json' };

import organizationEsCL from './locales/es-CL/organization.json' with { type: 'json' };
import scheduleEsCL from './locales/es-CL/schedule.json' with { type: 'json' };
import fractionalLeaveEsCL from './locales/es-CL/fractionalLeave.json' with { type: 'json' };
import securityGroupingEsCL from './locales/es-CL/securityGrouping.json' with { type: 'json' };
import auditEsCL from './locales/es-CL/audit.json' with { type: 'json' };
import checkInLocationEsCL from './locales/es-CL/checkInLocation.json' with { type: 'json' };
import asyncJobEsCL from './locales/es-CL/asyncJob.json' with { type: 'json' };
import deviceEsCL from './locales/es-CL/device.json' with { type: 'json' };
import paymentEsCL from './locales/es-CL/payment.json' with { type: 'json' };

/**
 * Month i18n keys for translation
 * Used with common:months namespace
 */
export const MONTH_I18N_KEYS = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
] as const;

export type MonthI18nKey = typeof MONTH_I18N_KEYS[number];

/**
 * Recursos de traducción organizados por locale y namespace
 * 
 * Base: es-CL (Español Chile)
 * 
 * Estructura expandible:
 * {
 *   'es-CL': { common, auth, employee, attendance, company },  // Base (Chile)
 *   'es-MX': { common, auth, ... },                            // México (solo diferencias)
 *   'es-PE': { common, auth, ... },                            // Perú (solo diferencias)
 *   'en-US': { common, auth, ... },                            // Inglés
 * }
 */
export const resources = {
  'es-CL': {
    common: commonEsCL,
    auth: authEsCL,
    employee: employeeEsCL,
    attendance: attendanceEsCL,
    company: companyEsCL,
    absence: absenceEsCL,
    authorization: authorizationEsCL,
    organization: organizationEsCL,
    schedule: scheduleEsCL,
    fractionalLeave: fractionalLeaveEsCL,
    securityGrouping: securityGroupingEsCL,
    audit: auditEsCL,
    checkInLocation: checkInLocationEsCL,
    asyncJob: asyncJobEsCL,
    device: deviceEsCL,
    payment: paymentEsCL,
  },
  // Preparado para agregar más locales:
  // 'es-MX': { ... },
  // 'es-PE': { ... },
  // 'en-US': { ... },
} as const;

/**
 * Configuración de fallback por locale
 * 
 * Base: es-CL (Español Chile)
 * Otros locales hispanos usan Chile como fallback
 * 
 * Ejemplo:
 * {
 *   'es-MX': ['es-CL'],  // Si falta en México, usa Chile
 *   'es-PE': ['es-CL'],  // Si falta en Perú, usa Chile
 *   'es-AR': ['es-CL'],  // Si falta en Argentina, usa Chile
 *   'default': ['es-CL'],
 * }
 */
const fallbackLng = {
  default: ['es-CL'],
};

export const NAMESPACES = [
  'common',
  'auth',
  'employee',
  'attendance',
  'company',
  'absence',
  'authorization',
  'organization',
  'schedule',
  'fractionalLeave',
  'audit',
  'checkInLocation',
  'asyncJob',
  'device',
  'payment',
] as const;

/**
 * Inicialización de i18next
 */
void i18n
  .use(LanguageDetector) // Detecta idioma del navegador/localStorage
  .use(initReactI18next) // Integración con React hooks
  .init({
    resources,
    fallbackLng,
    defaultNS: 'common', // Namespace por defecto
    ns: [...NAMESPACES],


    interpolation: {
      escapeValue: false, // React ya escapa por defecto
    },

    // Configuración de detección de idioma
    detection: {
      order: ['localStorage', 'navigator'], // Prioridad: localStorage > navegador
      caches: ['localStorage'], // Persistir selección en localStorage
      lookupLocalStorage: 'i18nextLng', // Key en localStorage
    },

    // Configuración de React
    react: {
      useSuspense: false, // No usar Suspense (traducciones síncronas)
    },

    // Desarrollo
    debug: false, // Cambiar a true para debug en desarrollo
  });

export default i18n;
